import { Link } from "react-router-dom";
import { ArrowRight, Eye, ShieldCheck, Siren, Users } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { track, useTrackPageView } from "@/lib/analytics";
import KineticTitle, { HeroFade } from "@/components/books/KineticTitle";
import Reveal from "@/components/books/Reveal";
import BookCover from "@/components/books/BookCover";
import BuyButton from "@/components/books/BuyButton";
import TrustFactsStrip from "@/components/books/TrustFactsStrip";
import TrustReviews from "@/components/books/TrustReviews";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  CYBER_BOOKS,
  CYBER_BUNDLE,
  CYBER_SAFETY_SERIES,
  CYBER_SAFETY_FAQ,
  CYBER_SAFETY_ACCENT as C,
  CYBER_SAFETY_TRUST_FACTS,
  CYBER_SAFETY_INFO_REVIEWED_LABEL,
  CYBER_SAFETY_TRUST_REVIEWS,
  CYBER_SAFETY_TRUST_REVIEWS_ENABLED,
} from "@/data/cyberSafetyBooks";

const WHY = [
  { Icon: Eye, t: "Recognise the pattern", d: "Scams borrow different names and apps, but the moves underneath repeat. Learn to see them." },
  { Icon: ShieldCheck, t: "Verify before acting", d: "One habit — checking independently before paying, clicking or sharing — closes most doors." },
  { Icon: Siren, t: "Know how to respond", d: "If something has already gone wrong, the first hour matters. We show exactly what to do." },
  { Icon: Users, t: "For the whole family", d: "Written to be read together — parents, grandparents and teens all recognise themselves here." },
];

export default function CyberSafetySeries() {
  useTrackPageView("cyber_series_view");
  useSEO({
    title: "LifeIQ Cyber Fraud & Scam Safety — A 3-Book Family Digital Safety Series",
    description: "Fraud awareness, digital protection and recovery for the whole family — three short digital books covering scam patterns, disguises used by fraud, and exactly what to do if something goes wrong.",
    canonicalPath: "/books/cyber-safety",
  });

  return (
    <main>
      {/* SERIES HERO */}
      <section className="relative overflow-hidden" style={{ backgroundColor: C.navy }}>
        <span aria-hidden="true" className="absolute -top-32 -right-24 w-[28rem] h-[28rem] rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: `${C.gold}22` }} />
        <span aria-hidden="true" className="absolute bottom-0 left-0 w-72 h-72 rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: `${C.teal}22` }} />
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-20 md:pt-28 pb-24 md:pb-32 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <HeroFade delay={0}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.gold }}>A LifeIQ Books Series</span>
                  <span className="text-white/30">·</span>
                  <span className="text-[12px] uppercase tracking-[0.24em] text-white/60 font-semibold">Family Digital Safety</span>
                </div>
              </HeroFade>
              <KineticTitle
                testId="cyber-series-hero-title"
                className="mt-6 font-serif-d text-[38px] sm:text-5xl lg:text-[60px] leading-[1.06] tracking-tight text-white"
                lines={["LifeIQ Cyber Fraud", "& Scam Safety"]}
              />
              <HeroFade delay={0.28}>
                <div data-testid="cyber-series-descriptor" className="mt-5 flex flex-wrap items-center gap-3">
                  <span className="text-[13px] sm:text-[15px] uppercase tracking-[0.14em] font-bold text-white/90">
                    {CYBER_SAFETY_SERIES.descriptor}
                  </span>
                </div>
                <p className="mt-2 text-[13px] sm:text-[14px] uppercase tracking-[0.1em] font-semibold" style={{ color: C.teal }}>
                  {CYBER_SAFETY_SERIES.supporting}
                </p>
              </HeroFade>
              <HeroFade delay={0.38}>
                <div data-testid="cyber-reflex-row" className="mt-6 flex items-center gap-2 flex-wrap">
                  {CYBER_SAFETY_SERIES.reflex.map((word, i) => (
                    <span key={word} className="flex items-center gap-2">
                      <span
                        className="text-[13px] sm:text-[14px] font-bold uppercase tracking-[0.12em] px-3 py-1.5 rounded-sm border"
                        style={{ color: word === "REPORT" ? C.red : C.gold, borderColor: word === "REPORT" ? `${C.red}55` : `${C.gold}55` }}
                      >
                        {word}
                      </span>
                      {i < CYBER_SAFETY_SERIES.reflex.length - 1 && <ArrowRight size={14} className="text-white/40" />}
                    </span>
                  ))}
                </div>
              </HeroFade>
              <HeroFade delay={0.46}>
                <p className="mt-6 text-[16px] sm:text-[17px] text-white/80 leading-[1.7] max-w-2xl">{CYBER_SAFETY_SERIES.intro}</p>
                <p className="mt-3 text-[13px] text-white/50">{CYBER_SAFETY_SERIES.use}</p>
                <div className="mt-10 flex flex-col sm:flex-row gap-3">
                  <a
                    href="#books"
                    data-testid="cyber-explore"
                    style={{ backgroundColor: C.gold }}
                    className="inline-flex items-center justify-center gap-2 text-[#0D1B2A] px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:opacity-90 transition-opacity duration-200 rounded-sm"
                  >
                    Explore the 3 Books <ArrowRight size={15} />
                  </a>
                  <Link
                    to="/books/cyber-safety/complete-series"
                    data-testid="cyber-bundle-quick-cta"
                    onClick={() => track("cyber_bundle_view", { source: "series-hero" })}
                    className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-white/40 px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:bg-white/10 transition-colors duration-200 rounded-sm"
                  >
                    Complete Series — {CYBER_BUNDLE.price}
                  </Link>
                </div>
                <div className="mt-8 pt-6 border-t border-white/10">
                  <TrustFactsStrip
                    facts={CYBER_SAFETY_TRUST_FACTS}
                    reviewedLabel={CYBER_SAFETY_INFO_REVIEWED_LABEL}
                    accentColor={C.gold}
                    textColor="rgba(255,255,255,0.65)"
                    dividerColor="rgba(255,255,255,0.25)"
                    testId="cyber-series-trust-facts"
                  />
                </div>
              </HeroFade>
            </div>
            <div className="lg:col-span-5">
              <HeroFade delay={0.5} className="grid grid-cols-3 gap-4 max-w-[440px] mx-auto">
                {CYBER_BOOKS.map((b) => (
                  <BookCover key={b.slug} book={b} tilt testId={`cyber-hero-cover-${b.slug}`} />
                ))}
              </HeroFade>
            </div>
          </div>
        </div>
      </section>

      {/* WHY THIS SERIES */}
      <section style={{ backgroundColor: C.cream }}>
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.gold }}>Why this series</div>
            <h2 className="mt-5 font-serif-d text-[32px] sm:text-5xl lg:text-[46px] leading-[1.08]" style={{ color: C.navy }}>
              Fraud changes its costume. The pattern underneath doesn't.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {WHY.map(({ Icon, t, d }, i) => (
              <Reveal key={t} delay={i * 0.07}>
                <div className="border rounded-sm p-6 h-full transition-colors duration-200" style={{ backgroundColor: C.creamSurface, borderColor: `${C.navy}1A` }}>
                  <div className="w-11 h-11 rounded-full text-white flex items-center justify-center" style={{ backgroundColor: C.navy }}><Icon size={20} /></div>
                  <div className="mt-5 font-serif-d text-[19px] leading-tight" style={{ color: C.navy }}>{t}</div>
                  <p className="mt-2 text-[14px] leading-[1.65]" style={{ color: C.textMuted }}>{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* THE 3-BOOK PROGRESSION */}
      <section className="bg-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.teal }}>The learning progression</div>
            <h2 className="mt-5 font-serif-d text-[32px] sm:text-5xl lg:text-[46px] leading-[1.08]" style={{ color: C.navy }}>Three books. One family reflex.</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="cyber-progression-grid">
            {CYBER_BOOKS.map((b) => (
              <Reveal key={b.slug} delay={b.book_number * 0.07}>
                <Link to={`/books/cyber-safety/${b.slug}`} className="block h-full">
                  <div className="h-full border rounded-sm p-7 hover:shadow-lg transition-shadow duration-200" style={{ borderColor: `${C.navy}1A` }}>
                    <div className="font-serif-d text-[38px] leading-none" style={{ color: `${C.navy}33` }}>{b.book_number}</div>
                    <div className="mt-3 text-[12px] font-bold uppercase tracking-[0.14em]" style={{ color: C.gold }}>{b.progression_label}</div>
                    <h3 className="mt-2 font-serif-d text-[21px] leading-[1.2]" style={{ color: C.navy }}>{b.title}</h3>
                    <p className="mt-3 text-[14px] leading-[1.6]" style={{ color: C.textMuted }}>{b.short_description}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* THE BOOKS — full journey */}
      <section id="books" style={{ backgroundColor: C.cream }}>
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.gold }}>The books</div>
            <h2 className="mt-5 font-serif-d text-[32px] sm:text-5xl lg:text-[46px] leading-[1.08]" style={{ color: C.navy }}>131 pages. Three short, practical guides.</h2>
          </Reveal>
          <div className="mt-16">
            {CYBER_BOOKS.map((b) => (
              <Reveal key={b.slug} delay={0.05}>
                <div data-testid={`cyber-journey-book-${b.book_number}`} className="grid grid-cols-12 gap-6 md:gap-10 py-10 border-t items-center" style={{ borderColor: `${C.navy}1A` }}>
                  <div className="col-span-12 sm:col-span-3 md:col-span-2">
                    <Link to={`/books/cyber-safety/${b.slug}`} aria-label={`View ${b.title}`}>
                      <BookCover book={b} tilt testId={`cyber-journey-cover-${b.slug}`} className="max-w-[180px]" />
                    </Link>
                  </div>
                  <div className="col-span-12 sm:col-span-9 md:col-span-7">
                    <div className="text-[11px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.gold }}>{b.label}</div>
                    <h3 className="mt-2 font-serif-d text-[24px] sm:text-[28px] leading-[1.12]" style={{ color: C.navy }}>{b.title}</h3>
                    <div className="mt-2.5 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.1em] px-3 py-1.5 rounded-sm" style={{ color: C.teal, backgroundColor: `${C.teal}14` }}>
                      {b.progression_label}
                    </div>
                    <p className="mt-3 text-[15px] leading-[1.65] max-w-xl" style={{ color: "#1A1A1A" }}>{b.short_description}</p>
                    <div className="mt-2 text-[13px] font-semibold" style={{ color: C.textMuted }}>{b.pages} pages · {b.language} · {b.format}</div>
                  </div>
                  <div className="col-span-12 md:col-span-3 flex md:flex-col items-center md:items-end gap-4">
                    <div className="text-left md:text-right">
                      <div className="mt-1 flex md:justify-end items-baseline gap-2">
                        <span className="text-[14px] line-through" style={{ color: "#9AA1AB", textDecorationColor: `${C.gold}B3` }}>{b.regular_price}</span>
                        <span className="font-serif-d text-[30px] leading-none" style={{ color: C.navy }}>{b.price}</span>
                      </div>
                      <div className="mt-1.5 flex md:justify-end">
                        <span className="text-[9px] uppercase tracking-[0.12em] font-bold px-1.5 py-0.5 rounded-sm" style={{ color: C.navy, backgroundColor: C.gold }}>{b.offer_label}</span>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row md:flex-col gap-2.5 w-full sm:w-auto md:items-end">
                      <Link
                        to={`/books/cyber-safety/${b.slug}`}
                        data-testid={`cyber-journey-view-${b.slug}`}
                        onClick={() => track("cyber_book_view", { product_id: b.product_id, source: "journey" })}
                        className="inline-flex items-center justify-center gap-2 border px-5 py-3 rounded-sm text-[12px] font-semibold uppercase tracking-wide hover:text-white transition-colors duration-200"
                        style={{ borderColor: C.navy, color: C.navy }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = C.navy; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                      >
                        Look Inside <ArrowRight size={14} />
                      </Link>
                      <BuyButton
                        product={b}
                        label={`Buy Digital Book — ${b.price}`}
                        source="cyber-journey"
                        testId={`cyber-journey-buy-${b.slug}`}
                        accentColor={C.gold}
                      />
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLETE SERIES BANNER */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <div data-testid="cyber-bundle-card" className="relative rounded-sm p-8 sm:p-12 lg:p-16 overflow-hidden" style={{ backgroundColor: C.navy }}>
              <span aria-hidden="true" className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: `${C.gold}22` }} />
              <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                <div className="md:col-span-7">
                  <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.gold }}>Complete series</div>
                  <h2 className="mt-4 font-serif-d text-[28px] sm:text-4xl lg:text-[42px] leading-[1.08] text-white">{CYBER_BUNDLE.title}</h2>
                  <p className="mt-5 text-[15px] sm:text-[17px] text-white/85 leading-[1.7] max-w-xl">{CYBER_BUNDLE.description}</p>
                  <div className="mt-6 flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-white/70">
                    <span>3 digital books · 131 pages</span>
                    <span>Format: {CYBER_BUNDLE.format}</span>
                  </div>
                  <div className="mt-7 flex items-baseline gap-2">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-semibold">{CYBER_BUNDLE.regular_price_label}</span>
                    <span className="text-[16px] text-white/40 line-through" style={{ textDecorationColor: `${C.gold}B3` }}>{CYBER_BUNDLE.regular_price}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-end gap-4">
                    <div className="font-serif-d text-[44px] leading-none" style={{ color: C.gold }}>{CYBER_BUNDLE.price}</div>
                    <div data-testid="cyber-bundle-savings-pill" className="pb-1.5 text-[12px] uppercase tracking-[0.14em] font-bold rounded-sm px-2.5 py-1" style={{ color: C.navy, backgroundColor: C.gold }}>
                      Save {CYBER_BUNDLE.savings}
                    </div>
                  </div>
                  <p className="mt-2 text-[12px] uppercase tracking-[0.14em] text-white/60 font-semibold">{CYBER_BUNDLE.offer_label}</p>
                  <div className="mt-5 flex flex-col sm:flex-row gap-3">
                    <BuyButton
                      product={CYBER_BUNDLE}
                      label={`Get All 3 Books — ${CYBER_BUNDLE.price}`}
                      source="cyber-series-bundle-banner"
                      testId="cyber-bundle-buy-button"
                      dark
                      accentColor={C.gold}
                    />
                    <Link
                      to="/books/cyber-safety/complete-series"
                      data-testid="cyber-bundle-details-link"
                      onClick={() => track("cyber_bundle_view", { source: "series-banner" })}
                      className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-6 py-4 rounded-sm text-[13px] font-semibold uppercase tracking-wide hover:bg-white/10 transition-colors duration-200"
                    >
                      See What's Inside <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
                <div className="md:col-span-5 grid grid-cols-3 gap-3">
                  {CYBER_BOOKS.map((b) => <BookCover key={b.slug} book={b} />)}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST / REVIEWS — prepared for future use, disabled until owner
          supplies genuine, consented reviewer/media content. Renders
          nothing today (CYBER_SAFETY_TRUST_REVIEWS_ENABLED === false). */}
      <TrustReviews
        items={CYBER_SAFETY_TRUST_REVIEWS}
        enabled={CYBER_SAFETY_TRUST_REVIEWS_ENABLED}
        accentColor={C.gold}
        navyColor={C.navy}
        mutedColor={C.textMuted}
      />

      {/* FAQ */}
      <section style={{ backgroundColor: C.cream }}>
        <div className="max-w-[920px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.teal }}>Questions</div>
            <h2 className="mt-5 font-serif-d text-[32px] sm:text-4xl lg:text-[42px] leading-[1.08]" style={{ color: C.navy }}>Common questions.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible data-testid="cyber-faq-accordion" className="mt-12 border-t" style={{ borderColor: `${C.navy}1A` }}>
              {CYBER_SAFETY_FAQ.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} style={{ borderColor: `${C.navy}1A` }}>
                  <AccordionTrigger data-testid={`cyber-faq-item-${i}`} className="font-serif-d text-[18px] hover:no-underline py-5" style={{ color: C.navy }}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-[1.7]" style={{ color: C.textMuted }}>{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 text-[13px]" style={{ color: C.textMuted }}>
              Fraud tactics and official reporting routes change over time. See our{" "}
              <Link to="/books/cyber-safety/updates" className="underline font-semibold" style={{ color: C.teal }}>
                Updates & Official Resources
              </Link>{" "}
              page for the latest.
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
